<template>
  <div ref="mapRef" v-bind="$attrs" :class="{ 'opacity-30': !locations.length }" />
  <MapInfoWindow v-bind="infoWindowProps" ref="infoWindowRef" />
</template>

<script setup lang="ts">
import type { Location } from '@/types/store';

type LocationData = {
  text: string;
  lat: number;
  lng: number;
  name?: string;
  address?: string;
  storeId?: string;
  id?: string;
};

type Props = {
  locations?: LocationData[];
  focusedLocation?: Location;
  currentLocation?: Location;
  searchString?: string;
  extendedInfoWindow?: boolean;
};

const DEFAULT_ZOOM = 15;
const DEFAULT_MIN_ZOOM = 5;
// DAHL Bromma as default
const DEFAULT_LOCATION = {
  lat: 59.34795995833531,
  lng: 17.96478313095946,
};
const RESULTS_OK = 'OK';
const ZERO_RESULTS = 'ZERO_RESULTS';

const props = withDefaults(defineProps<Props>(), {
  locations: () => [],
  focusedLocation: () => ({ lat: 0, lng: 0 }),
  currentLocation: () => ({ lat: 0, lng: 0 }),
  searchString: '',
  extendedInfoWindow: false,
});

const emits = defineEmits<{
  (e: 'set-search-result', event: string[]): void;
}>();

const config = useRuntimeConfig();
const { $sentry, $googleMaps } = useNuxtApp();
const { mq } = useMediaQueries();
const mapRef = ref(null);
const infoWindowRef = ref<ComponentPublicInstance<HTMLElement> | null>(null);
const map = shallowRef<google.maps.Map | null>(null);
const markers = ref<google.maps.marker.AdvancedMarkerElement[]>([]);
const hasInitiated = ref(false);
const geocoder = ref<google.maps.Geocoder | null>(null);
const infoWindow = ref<google.maps.InfoWindow | null>(null);
const infoWindowProps = ref({});

watch($googleMaps?.hasLoaded, () => init());

watch(
  () => props.locations,
  () => {
    if (!map.value) return;
    handleMarkers();
  },
);
watch(
  () => props.focusedLocation,
  () => {
    if (!map.value) return;
    if (props.focusedLocation.lat && props.focusedLocation.lng) {
      const latLng = new google.maps.LatLng(props.focusedLocation);
      panToLocation(latLng, DEFAULT_ZOOM);
    } else {
      setBounds();
    }
  },
);
watch(
  () => props.currentLocation,
  () => {
    if (!map.value) return;
    const latLng = new google.maps.LatLng(props.currentLocation);
    currentLocationSearch(latLng);
  },
);

watch(
  () => props.searchString,
  () => {
    if (!map.value) return;
    if (props.searchString) {
      const results = searchInLocations();

      if (results.length) {
        const location = results[0];
        const latLng = new google.maps.LatLng(location.lat, location.lng);

        currentLocationSearch(latLng);

        const ids = results.reduce((acc, result) => {
          const id = result.storeId || result.id;
          if (id) {
            acc.push(id);
          }
          return acc;
        }, [] as string[]);

        emits('set-search-result', ids);

        openMapInfoWindow(results[0], latLng);
      } else {
        geocoder.value?.geocode(
          {
            address: props.searchString,
            componentRestrictions: {
              country: 'SE',
            },
          },
          search,
        );
      }
    }
  },
);

onMounted(() => {
  loadGoogleMaps(config.public, init);
});

const searchInLocations = () => {
  return props.locations.filter((location) => {
    return (
      (!!location.name?.length && location.name.toLowerCase().includes(props.searchString.toLowerCase())) ||
      (!!location.address?.length && location.address.toLowerCase().includes(props.searchString.toLowerCase()))
    );
  });
};

const openMapInfoWindow = (store: LocationData, location: google.maps.LatLng) => {
  const marker = getMarker(location);
  const infoWindowContent = createInfoWindowContent(store);
  infoWindow.value?.setContent(infoWindowContent);
  infoWindow.value?.open(map.value, marker);
};

const getMarker = (location: google.maps.LatLng) => {
  return markers.value.find((marker) => {
    const latLng = marker.position;
    return latLng?.lat === location.lat() && latLng?.lng === location.lng();
  });
};

const createMap = () => {
  const mapOptions = {
    zoom: DEFAULT_ZOOM,
    center: DEFAULT_LOCATION,
    disableDefaultUI: true,
    clickableIcons: true,
    minZoom: DEFAULT_MIN_ZOOM,
    zoomControl: true,
    mapId: config.public.googleMapsId as string,
  };

  map.value = new google.maps.Map(mapRef.value!, mapOptions);
  geocoder.value = new google.maps.Geocoder();
  infoWindow.value = new google.maps.InfoWindow();
};

const handleMarkers = () => {
  // Clear all markers from map before adding new markers
  clearMarkers();

  if (!props.locations.length) {
    map.value?.setOptions({ gestureHandling: 'none' });
    return;
  }

  map.value?.setOptions({ gestureHandling: 'auto' });

  markers.value = props.locations.map((location) => {
    const colors = location.storeId
      ? {
          background: 'var(--color-primary-700)',
          borderColor: 'var(--color-primary-900)',
          glyphColor: 'var(--color-primary-900)',
        }
      : {
          background: '#b91c1c',
          borderColor: '#7f1d1d',
          glyphColor: '#7f1d1d',
        };

    const pinElement = new google.maps.marker.PinElement(colors);
    const position = new google.maps.LatLng(location.lat, location.lng);
    const rawMap = toRaw(map.value);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      map: rawMap,
      title: location.text,
      content: pinElement.element,
    });

    // @ts-ignore
    marker.storeId = location.storeId || location.id;

    marker.addListener('click', () => {
      const infoWindowContent = createInfoWindowContent(location);
      infoWindow.value?.setContent(infoWindowContent);
      infoWindow.value?.open(rawMap, marker);
      const latLng = marker.position;
      if (latLng) {
        map.value?.panTo(latLng);
      }
    });
    return marker;
  });

  if (markers.value.length > 1) {
    setBounds();
  } else {
    map.value?.setCenter({ lat: props.locations[0].lat, lng: props.locations[0].lng });
    map.value?.setZoom(DEFAULT_ZOOM);
  }
};

const setBounds = () => {
  // Sets the viewport to contain all markers
  const bounds = new google.maps.LatLngBounds();
  markers.value.forEach((marker) => {
    if (marker.position) {
      bounds.extend(marker.position!);
    }
  });
  map.value?.fitBounds(bounds);
};

const createInfoWindowContent = (location: LocationData) => {
  if (!props.extendedInfoWindow) {
    return location.text;
  }
  infoWindowProps.value = { ...location };
  infoWindow.value?.setOptions({ minWidth: 300 });

  return infoWindowRef.value?.$el;
};

const clearMarkers = () => {
  markers.value.forEach((marker) => {
    marker.map = null;
    marker.position = null;
  });
  markers.value = [];
};

const init = () => {
  if ($googleMaps?.hasLoaded.value && !hasInitiated.value && mapRef.value) {
    createMap();
    window.mapInstance = map.value;
    handleMarkers();
    hasInitiated.value = true;
  }
};

const panToLocation = (location: google.maps.LatLng, zoomLevel: number) => {
  infoWindow.value?.close();
  map.value?.panTo(location);
  map.value?.setZoom(zoomLevel);
};

const findStoresInViewport = (location: google.maps.LatLng) => {
  const currentBounds = map.value?.getBounds();
  return markers.value
    .filter((marker) => (currentBounds && marker.position ? currentBounds.contains(marker.position!) : false))
    .map((marker) => ({
      // @ts-ignore
      storeId: marker.storeId,
      distance: google.maps.geometry.spherical.computeDistanceBetween(location, marker.position!),
    }))
    .sort((a, b) => (a.distance > b.distance ? 1 : -1))
    .map((store) => store.storeId);
};

const search = (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
  if (status === RESULTS_OK && results && results.length) {
    const location = results[0].geometry.location;
    currentLocationSearch(location);
    const searchResult = findStoresInViewport(location);
    emits('set-search-result', searchResult);
  } else if (status === ZERO_RESULTS) {
    setBounds();
    emits('set-search-result', []);
  } else {
    $sentry.captureException(status);
    createError({ statusCode: 500, message: 'Unknown map error' });
  }
};

const currentLocationSearch = (location: google.maps.LatLng) => {
  const zoomLevel = mq.value.xlarge || mq.value.xxlarge ? 11 : 10;
  panToLocation(location, zoomLevel);
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'GoogleMap',
});
</script>
